import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the navigation item structure
export interface NavigationItem {
  id: string;
  name: string;
  path: string;
  icon: React.ComponentType<any>;
  description?: string;
  children?: NavigationItem[];
}

// Define the navigation context properties
interface NavigationContextProps {
  navigationItems: NavigationItem[];
  activeItem: string;
  setActiveItem: (id: string) => void;
  breadcrumbs: { label: string; path: string }[];
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}

// Create the context with default values
const NavigationContext = createContext<NavigationContextProps | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
  items: NavigationItem[];
  defaultActiveItem?: string;
}

/**
 * Navigation Provider component that manages navigation state
 * and provides it to all child components
 */
export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  items,
  defaultActiveItem
}) => {
  // State for active navigation item
  const [activeItem, setActiveItem] = useState<string>(defaultActiveItem || (items[0]?.id || ''));
  
  // State for breadcrumbs
  const [breadcrumbs, setBreadcrumbs] = useState<{ label: string; path: string }[]>([]);
  
  // State for sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1200);
  
  // State for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Function to toggle sidebar
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  
  // Function to toggle mobile menu
  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);

  // Update breadcrumbs when active item changes
  useEffect(() => {
    const updateBreadcrumbs = () => {
      const findItem = (
        items: NavigationItem[],
        id: string
      ): { item: NavigationItem | null; parents: NavigationItem[] } => {
        for (const item of items) {
          if (item.id === id) {
            return { item, parents: [] };
          }
          
          if (item.children?.length) {
            const result = findItem(item.children, id);
            if (result.item) {
              return { item: result.item, parents: [item, ...result.parents] };
            }
          }
        }
        
        return { item: null, parents: [] };
      };
      
      const { item, parents } = findItem(items, activeItem);
      
      if (item) {
        const newBreadcrumbs = [
          { label: 'Home', path: '/' },
          ...parents.map(parent => ({
            label: parent.name,
            path: parent.path
          })),
          { label: item.name, path: item.path }
        ];
        
        setBreadcrumbs(newBreadcrumbs);
      } else {
        setBreadcrumbs([{ label: 'Home', path: '/' }]);
      }
    };
    
    updateBreadcrumbs();
  }, [activeItem, items]);
  
  // Listen for screen size changes to adjust sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else if (window.innerWidth >= 1200) {
        setIsSidebarOpen(true);
      }
      
      // Close mobile menu on resize
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Context value
  const value = {
    navigationItems: items,
    activeItem,
    setActiveItem,
    breadcrumbs,
    isSidebarOpen,
    toggleSidebar,
    isMobileMenuOpen,
    toggleMobileMenu
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Custom hook to use the navigation context
 */
export const useNavigation = (): NavigationContextProps => {
  const context = useContext(NavigationContext);
  
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  
  return context;
}; 